import type { ChartConfig } from '@/components/ui/chart';

/** Display names for known AI coding tools */
const TOOL_DISPLAY_NAMES: Record<string, string> = {
  'claude': 'Claude',
  'claude-code': 'Claude Code',
  'claude code': 'Claude Code',
  'cursor': 'Cursor',
  'copilot': 'Copilot',
  'github-copilot': 'GitHub Copilot',
  'github copilot': 'GitHub Copilot',
  'codex': 'Codex',
  'codex-cli': 'Codex',
  'openai-codex': 'Codex',
  'openai codex': 'Codex',
  'droid': 'Droid',
  'windsurf': 'Windsurf',
  'aider': 'Aider',
  'opencode': 'OpenCode',
  'codeium': 'Codeium',
  'continue': 'Continue',
  'continue.dev': 'Continue',
  'other': 'Other',
};

/** Get the display name for a tool, capitalizing unknown tools */
export function getToolDisplayName(tool: string): string {
  const displayName = TOOL_DISPLAY_NAMES[tool.toLowerCase()];
  if (displayName) {
    return displayName;
  }
  // Capitalize first letter of each word for unknown tools
  return tool.replace(/\b\w/g, (c) => c.toUpperCase());
}

export const CHART_ACCENT_COLORS = {
  teal: 'hsl(170 65% 45%)',
  cyan: 'hsl(190 70% 48%)',
  lime: 'hsl(95 50% 48%)',
  violet: 'hsl(280 65% 60%)',
  rose: 'hsl(340 75% 55%)',
} as const;

/** Shared palette for AI Review and Delivery & effectiveness graphs. */
export const ANALYTICS_CHART_COLORS = {
  blue: 'hsl(var(--chart-1))',
  green: 'hsl(var(--analytics-green))',
  purple: 'hsl(var(--chart-4))',
  orange: 'hsl(var(--chart-3))',
  yellow: 'hsl(48 90% 55%)',
  ...CHART_ACCENT_COLORS,
} as const;

/**
 * Predefined colors for known AI coding tools.
 *
 * Uses hardcoded HSL values chosen for maximum perceptual distance between the
 * most popular tools. The first 8 entries cover the tools that appear most
 * frequently in customer charts and must remain easy to tell apart at a glance.
 */
export const TOOL_COLORS: Record<string, string> = {
  // ── Popular tools (maximally distinct) ──────────────────────────
  'claude': 'hsl(25 90% 55%)', // Warm orange
  'claude-code': 'hsl(25 90% 55%)', // Warm orange (alias)
  'claude code': 'hsl(25 90% 55%)', // Warm orange (alias)
  'cursor': CHART_ACCENT_COLORS.teal,
  'copilot': 'hsl(130 50% 42%)', // Green
  'github-copilot': 'hsl(130 50% 42%)', // Green (alias)
  'github copilot': 'hsl(130 50% 42%)', // Green (alias)
  'codex': 'hsl(220 75% 56%)', // Blue
  'openai-codex': 'hsl(220 75% 56%)', // Blue (alias)
  'openai codex': 'hsl(220 75% 56%)', // Blue (alias)
  'opencode': 'hsl(52 90% 48%)', // Yellow
  'windsurf': 'hsl(280 65% 60%)', // Purple
  'aider': 'hsl(340 75% 55%)', // Pink / rose
  'droid': CHART_ACCENT_COLORS.cyan,
  // ── Less common tools ───────────────────────────────────────────
  'codeium': 'hsl(140 55% 42%)', // Forest green
  'continue': 'hsl(310 55% 52%)', // Magenta
  'continue.dev': 'hsl(310 55% 52%)', // Magenta (alias)
  'other': 'hsl(var(--muted-foreground))', // Muted foreground
};

/**
 * Fallback palette for unknown tools. Intentionally avoids hues already used
 * in TOOL_COLORS so that dynamically-added tools don't clash with the popular
 * predefined ones.
 */
const FALLBACK_PALETTE: [string, ...string[]] = [
  CHART_ACCENT_COLORS.lime,
  'hsl(200 60% 50%)', // Sky blue
  'hsl(350 60% 48%)', // Crimson
  'hsl(265 50% 55%)', // Indigo
  'hsl(40 70% 50%)', // Amber
  'hsl(175 55% 42%)', // Dark teal
  'hsl(320 50% 50%)', // Plum
];

/**
 * Get a color for a tool. Uses predefined colors for known tools,
 * falls back to a palette for unknown tools based on index.
 */
export function getToolColor(tool: string, index = 0): string {
  const normalized = tool.toLowerCase();
  const predefinedColor = TOOL_COLORS[normalized];
  if (predefinedColor) {
    return predefinedColor;
  }
  // Fallback for unknown tools - using non-null assertion since FALLBACK_PALETTE always has elements
  return FALLBACK_PALETTE[index % FALLBACK_PALETTE.length]!;
}

/**
 * Build a ChartConfig for a list of tools.
 * Used by ChartContainer to set up CSS variables for colors.
 */
export function buildToolChartConfig(tools: string[]): ChartConfig {
  const config: ChartConfig = {};
  tools.forEach((tool, index) => {
    config[tool] = {
      label: getToolDisplayName(tool),
      color: getToolColor(tool, index),
    };
  });
  return config;
}
